import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { Typography, Flex, Space, Button, Input, InputNumber, Select, Radio, Switch, Segmented, Dropdown, ColorPicker, Card, Row, Col, Divider, Tag, Tooltip, Popconfirm, Empty, Spin, Modal, Upload, message } from "antd";
import {
  ArrowLeftOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  SaveOutlined,
  DownloadOutlined,
  UndoOutlined,
  RedoOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  LockOutlined,
  UnlockOutlined,
  FontSizeOutlined,
  PictureOutlined,
  FilePdfOutlined,
  BorderOutlined,
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  UnorderedListOutlined,
  AppstoreOutlined,
  SettingOutlined,
  BarsOutlined,
  SearchOutlined,
  ExpandOutlined,
  TagOutlined,
  DragOutlined,
  FileTextOutlined,
  CompressOutlined,
  LeftOutlined,
  RightOutlined,
  CopyOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MoreOutlined,
  UpOutlined,
  DownOutlined,
} from "@ant-design/icons";
import Konva from "konva";
import { PDFDocument, StandardFonts } from "pdf-lib";
import { getProposalTemplates, getProposalTemplateById, saveProposalTemplate, deleteProposalTemplate } from "../../../services/organizationService";
import useAuthSession from "../../../hooks/useAuthSession";
import { API_BASE_URL } from "../../../config/api";
import { FloppyDiskIcon } from "hugeicons-react";
import "./ProposalSettingsSection.css";

const { Title, Text } = Typography;

function resolveAssetUrl(url) {
  if (!url || typeof url !== "string") return "";
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) return url;
  if (url.startsWith("/uploads")) return `${API_BASE_URL}${url}`;
  return url;
}

const A4_W = 794;
const A4_H = 1123;
const A4_PTS_W = 595.28;
const A4_PTS_H = 841.89;

const FONT_FAMILIES = ["Arial", "Inter", "Helvetica", "Verdana", "Roboto"];
const FONT_FAMILY_OPTIONS = FONT_FAMILIES.map((ff) => ({ value: ff, label: ff }));

const LEAD_DB_FIELDS = [
  "companyName",
  "contactName",
  "contactNumber",
  "email",
  "dateOfIncorporation",
  "companyEmail",
  "authorisedCapital",
  "paidUpCapital",
  "companyType",
  "classOfCompany",
  "companyOrigin",
  "roc",
  "directorFirstName",
  "directorLastName",
  "directorMobileNumber",
  "directorEmailAddress",
  "cin",
  "totalObligationOfContribution",
  "addressType",
  "streetAddressLine1",
  "streetAddressLine2",
  "city",
  "state",
  "postalCode",
  "registrationNumber",
];

const PLACEHOLDER_SELECT_OPTIONS = [
  { value: "", label: "Select lead field...", disabled: true },
  {
    label: "Lead Fields",
    options: LEAD_DB_FIELDS.map((f) => ({ value: f, label: f })),
  },
  {
    label: "Custom Options",
    options: [
      { value: "custom", label: "Custom Text Placeholder..." },
      { value: "dropdown", label: "Dropdown (Pill Tags)..." },
    ],
  },
];

// Helper to safely load PDF.js from window or inject CDN script if needed
function loadPdfJs() {
  return new Promise((resolve, reject) => {
    if (window.pdfjsLib) {
      return resolve(window.pdfjsLib);
    }
    const existingScript = document.getElementById("pdfjs-cdn-script");
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(window.pdfjsLib));
      existingScript.addEventListener("error", reject);
      return;
    }
    const script = document.createElement("script");
    script.id = "pdfjs-cdn-script";
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
    script.integrity = "sha512-q+4liFwdPC/bNdhUpZx6aXDx/h77yEQtn4I1slHydcbZK34nLaR3cAeYSJshoxIOq3mjEf7xJE8YWIUHMn+oCQ==";
    script.crossOrigin = "anonymous";
    script.onload = () => {
      if (window.pdfjsLib) {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
        resolve(window.pdfjsLib);
      } else {
        reject(new Error("pdfjsLib not found on window"));
      }
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

function hexToRgbPdf(hex) {
  const clean = (hex || "#000000").replace("#", "");
  const r = Number.parseInt(clean.substring(0, 2), 16) / 255;
  const g = Number.parseInt(clean.substring(2, 4), 16) / 255;
  const b = Number.parseInt(clean.substring(4, 6), 16) / 255;
  return { type: "RGB", red: Number.isNaN(r) ? 0 : r, green: Number.isNaN(g) ? 0 : g, blue: Number.isNaN(b) ? 0 : b };
}

function wrapTextForPdf(text, font, fontSize, maxWidth) {
  const rawLines = text.split(/\r?\n/);
  const lines = [];
  for (const rawLine of rawLines) {
    const words = rawLine.split(" ");
    let line = "";
    for (const word of words) {
      const test = line ? `${line} ${word}` : word;
      if (font.widthOfTextAtSize(test, fontSize) > maxWidth && line) {
        lines.push(line);
        line = word;
      } else {
        line = test;
      }
    }
    if (line) lines.push(line);
    else if (!words.length || (words.length === 1 && words[0] === "")) lines.push("");
  }
  return lines;
}

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunk = 8192;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCodePoint(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function base64ToUint8Array(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.codePointAt(i);
  return bytes;
}

function createKonvaTextNode(layer) {
  const node = new Konva.Group({
    id: layer.id,
    x: layer.x,
    y: layer.y,
    width: layer.w,
    height: layer.h,
    draggable: !layer.locked,
  });
  node.add(
    new Konva.Rect({
      name: "bg",
      width: layer.w,
      height: layer.h,
      fill: layer.fillColor || "transparent",
      cornerRadius: layer.borderRadius || 0,
    }),
  );
  node.add(
    new Konva.Text({
      name: "txt",
      width: layer.w,
      height: layer.h,
      text: layer.isPlaceholder ? `[${layer.placeholderLabel || "Placeholder"}]` : layer.content || "Text",
      fontSize: layer.fontSize || 16,
      fontFamily: layer.fontFamily || "Inter",
      fill: layer.color || "#000000",
      fontStyle: `${layer.bold ? "bold" : ""} ${layer.italic ? "italic" : ""}`.trim() || "normal",
      align: layer.align || "left",
      verticalAlign: "top",
      padding: layer.padding !== undefined ? layer.padding : 4,
      lineHeight: layer.lineHeight || 1.2,
      opacity: 0, // HTML rich overlay provides crisp rendering
    }),
  );
  return node;
}

function createKonvaImageNode(layer, onImageLoaded) {
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.src = resolveAssetUrl(layer.src);
  const node = new Konva.Image({
    id: layer.id,
    x: layer.x,
    y: layer.y,
    width: layer.w,
    height: layer.h,
    image: img,
    draggable: !layer.locked,
  });
  img.onload = () => {
    if (typeof onImageLoaded === "function") {
      onImageLoaded();
    }
  };
  return node;
}

function createKonvaShapeNode(layer) {
  if (layer.shapeType === "circle") {
    return new Konva.Ellipse({
      id: layer.id,
      x: layer.x + layer.w / 2,
      y: layer.y + layer.h / 2,
      radiusX: layer.w / 2,
      radiusY: layer.h / 2,
      fill: layer.fillColor || "transparent",
      stroke: layer.strokeColor || "#475569",
      strokeWidth: layer.strokeWidth || 2,
      draggable: !layer.locked,
    });
  }
  if (layer.shapeType === "line") {
    return new Konva.Line({
      id: layer.id,
      x: layer.x,
      y: layer.y,
      points: [0, 0, layer.w, 0],
      stroke: layer.strokeColor || "#475569",
      strokeWidth: layer.strokeWidth || 2,
      draggable: !layer.locked,
    });
  }
  return new Konva.Rect({
    id: layer.id,
    x: layer.x,
    y: layer.y,
    width: layer.w,
    height: layer.h,
    fill: layer.fillColor || "transparent",
    stroke: layer.strokeColor || "#475569",
    strokeWidth: layer.strokeWidth || 2,
    cornerRadius: layer.borderRadius || 0,
    draggable: !layer.locked,
  });
}

function createKonvaLayerNode(layer, onImageLoaded) {
  if (layer.type === "text") {
    return createKonvaTextNode(layer);
  }
  if (layer.type === "image" || layer.type === "pdf_page") {
    return createKonvaImageNode(layer, onImageLoaded);
  }
  return createKonvaShapeNode(layer);
}

function updateTextNodeDimensions(node, targetW, targetH) {
  const bg = node.findOne(".bg");
  if (bg) {
    bg.width(targetW);
    bg.height(targetH);
  }
  const txt = node.findOne(".txt");
  if (txt) {
    txt.width(targetW);
    txt.height(targetH);
  }
}

function updateLayerPositionInPages(prevPages, pageIndex, layerId, x, y) {
  const nextPages = structuredClone(prevPages);
  const target = nextPages[pageIndex]?.layers?.find((item) => item.id === layerId);
  if (target) {
    target.x = x;
    target.y = y;
  }
  return nextPages;
}

function updateLayerTransformInPages(prevPages, pageIndex, layerId, transformData) {
  const nextPages = structuredClone(prevPages);
  const target = nextPages[pageIndex]?.layers?.find((item) => item.id === layerId);
  if (target) {
    target.x = transformData.x;
    target.y = transformData.y;
    target.w = transformData.w;
    target.h = transformData.h;
  }
  return nextPages;
}

function attachKonvaLayerEvents(node, layer, { onSelect, onDragEnd, onTransformEnd }) {
  node.on("click tap", () => onSelect(layer.id));
  node.on("dragend", () => {
    onDragEnd(layer.id, Math.round(node.x()), Math.round(node.y()));
  });
  node.on("transformend", () => {
    const w = Math.round(node.width() * node.scaleX());
    const h = Math.round(node.height() * node.scaleY());
    node.scaleX(1);
    node.scaleY(1);
    node.width(w);
    node.height(h);
    if (layer.type === "text") {
      updateTextNodeDimensions(node, w, h);
    }
    onTransformEnd(layer.id, {
      x: Math.round(node.x()),
      y: Math.round(node.y()),
      w,
      h,
    });
  });
}

function resolveProperty(overrideVal, currentVal) {
  return overrideVal !== undefined ? overrideVal : currentVal;
}

function computeFinalFillColor(override, propHasBackground, propFillColor) {
  const hasBg = resolveProperty(override.hasBackground, propHasBackground);
  if (!hasBg) return "transparent";
  return override.fillColor || propFillColor;
}

function resolvePlaceholderConfig(override, propState) {
  const pMode = resolveProperty(override.placeholderMode, propState.propPlaceholderMode);
  const isCustomOrDropdown = pMode === "custom" || pMode === "dropdown";
  const label = isCustomOrDropdown ? resolveProperty(override.placeholderLabel, propState.propPlaceholderLabel) : pMode;

  let dropdownOptions;
  if (pMode === "dropdown") {
    const rawText = resolveProperty(override.dropdownOptionsText, propState.propPlaceholderDropdownOptionsText);
    dropdownOptions = (rawText || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }

  return { label, dropdownOptions };
}

function buildTextLayerUpdates(override, propState, finalFillColor) {
  const { label, dropdownOptions } = resolvePlaceholderConfig(override, propState);

  return {
    content: resolveProperty(override.content, propState.propContent),
    fontSize: resolveProperty(override.fontSize, propState.propFontSize),
    fontFamily: resolveProperty(override.fontFamily, propState.propFontFamily),
    color: resolveProperty(override.color, propState.propColor),
    align: resolveProperty(override.align, propState.propAlign),
    bold: resolveProperty(override.bold, propState.propBold),
    italic: resolveProperty(override.italic, propState.propItalic),
    underline: resolveProperty(override.underline, propState.propUnderline),
    lineHeight: resolveProperty(override.lineHeight, propState.propLineHeight),
    letterSpacing: resolveProperty(override.letterSpacing, propState.propLetterSpacing),
    isPlaceholder: resolveProperty(override.isPlaceholder, propState.propIsPlaceholder),
    placeholderLabel: label,
    placeholderMaxChars: resolveProperty(override.placeholderMaxChars, propState.propPlaceholderMaxChars),
    placeholderDropdownOptions: dropdownOptions,
    fillColor: finalFillColor,
    borderRadius: resolveProperty(override.borderRadius, propState.propBorderRadius),
    padding: resolveProperty(override.padding, propState.propPadding),
  };
}

function buildShapeLayerUpdates(override, propState, finalFillColor) {
  return {
    fillColor: finalFillColor,
    strokeColor: resolveProperty(override.strokeColor, propState.propStrokeColor),
    strokeWidth: resolveProperty(override.strokeWidth, propState.propStrokeWidth),
    borderRadius: resolveProperty(override.borderRadius, propState.propBorderRadius),
  };
}

function applyLayerProperties(target, override, propState, finalFillColor) {
  if (target.type === "text") {
    Object.assign(target, buildTextLayerUpdates(override, propState, finalFillColor));
  } else if (target.type === "shape") {
    Object.assign(target, buildShapeLayerUpdates(override, propState, finalFillColor));
  }
}

function updateLayerPropsInPages(prevPages, pageIndex, layerId, override, propState) {
  const nextPages = structuredClone(prevPages);
  const target = nextPages[pageIndex]?.layers?.find((x) => x.id === layerId);
  if (!target) return prevPages;

  const finalFillColor = computeFinalFillColor(override, propState.propHasBackground, propState.propFillColor);
  applyLayerProperties(target, override, propState, finalFillColor);
  return nextPages;
}

function updateLayerDimensionsInPages(prevPages, pageIndex, layerId, width, height) {
  const nextPages = structuredClone(prevPages);
  const target = nextPages[pageIndex]?.layers?.find((x) => x.id === layerId);
  if (target) {
    target.w = width;
    target.h = height;
  }
  return nextPages;
}

function resolvePdfFontKey(family, bold, italic) {
  const normFamily = (family || "").toLowerCase();
  if (normFamily.includes("courier")) {
    if (bold && italic) return StandardFonts.CourierBoldOblique;
    if (bold) return StandardFonts.CourierBold;
    if (italic) return StandardFonts.CourierOblique;
    return StandardFonts.Courier;
  }
  if (bold && italic) return StandardFonts.HelveticaBoldOblique;
  if (bold) return StandardFonts.HelveticaBold;
  if (italic) return StandardFonts.HelveticaOblique;
  return StandardFonts.Helvetica;
}

function stripHtmlTags(str) {
  if (!str?.includes("<")) return str || "";
  return str
    .split("<")
    .map((part, i) => (i === 0 ? part : part.substring(part.indexOf(">") + 1)))
    .join("");
}

function cleanRawText(rawText) {
  const formatted = (rawText || "").replace(/<br\s*\/?>/gi, "\n").replace(/<\/p>|<\/div>/gi, "\n");
  return stripHtmlTags(formatted)
    .replace(/\n\s*\n/g, "\n")
    .trim();
}

function calculatePdfTextDrawX(align, lx, lw, padX, font, line, fontSize) {
  if (align === "center") {
    return lx + (lw - font.widthOfTextAtSize(line, fontSize)) / 2;
  }
  if (align === "right") {
    return lx + lw - font.widthOfTextAtSize(line, fontSize) - padX;
  }
  return lx + padX;
}

async function renderPdfTextLayer(doc, pdfPage, layer, bounds) {
  const { lx, ly, lw, lh, scaleX, scaleY } = bounds;
  if (layer.fillColor && layer.fillColor !== "transparent") {
    pdfPage.drawRectangle({
      x: lx,
      y: ly,
      width: lw,
      height: lh,
      color: hexToRgbPdf(layer.fillColor),
    });
  }

  const fontKey = resolvePdfFontKey(layer.fontFamily, layer.bold, layer.italic);
  const font = await doc.embedFont(fontKey);
  const fontSize = (layer.fontSize || 16) * scaleY;
  const color = hexToRgbPdf(layer.color || "#000000");

  const raw = layer.isPlaceholder ? `[${layer.placeholderLabel || "Placeholder"}]` : layer.content || "";
  const cleanedText = cleanRawText(raw);

  const pad = layer.padding !== undefined ? layer.padding : 4;
  const padX = pad * scaleX;
  const padY = pad * scaleY;
  const maxW = lw - padX * 2;
  const lines = wrapTextForPdf(cleanedText, font, fontSize, maxW);
  const lineH = fontSize * 1.3;
  const baselineOffset = font.heightAtSize(fontSize, { descender: false });
  let textY = ly + lh - padY - baselineOffset;

  for (const line of lines) {
    if (textY < ly) break;
    const drawX = calculatePdfTextDrawX(layer.align, lx, lw, padX, font, line, fontSize);
    pdfPage.drawText(line, { x: drawX, y: textY, size: fontSize, font, color });
    textY -= lineH;
  }
}

function renderPdfShapeLayer(pdfPage, layer, bounds) {
  const { lx, ly, lw, lh, scaleX, scaleY } = bounds;
  const fill = layer.fillColor && layer.fillColor !== "transparent" ? hexToRgbPdf(layer.fillColor) : undefined;
  const stroke = hexToRgbPdf(layer.strokeColor || "#475569");
  const sw = (layer.strokeWidth || 2) * Math.min(scaleX, scaleY);

  if (layer.shapeType === "circle") {
    pdfPage.drawEllipse({
      x: lx + lw / 2,
      y: ly + lh / 2,
      xScale: lw / 2,
      yScale: lh / 2,
      color: fill,
      borderColor: stroke,
      borderWidth: sw,
      opacity: fill ? 1 : 0,
      borderOpacity: 1,
    });
  } else if (layer.shapeType === "line") {
    pdfPage.drawLine({
      start: { x: lx, y: ly + lh / 2 },
      end: { x: lx + lw, y: ly + lh / 2 },
      thickness: sw,
      color: stroke,
    });
  } else {
    pdfPage.drawRectangle({
      x: lx,
      y: ly,
      width: lw,
      height: lh,
      color: fill,
      borderColor: stroke,
      borderWidth: sw,
      opacity: fill ? 1 : 0,
      borderOpacity: 1,
    });
  }
}

async function renderPdfImageLayer(doc, pdfPage, layer, bounds) {
  if (!layer.src) return;
  const { lx, ly, lw, lh } = bounds;
  try {
    let bytes;
    let isPng = false;
    if (layer.src.startsWith("/uploads") || layer.src.startsWith("http")) {
      const fullUrl = resolveAssetUrl(layer.src);
      const res = await fetch(fullUrl);
      bytes = new Uint8Array(await res.arrayBuffer());
      const ct = res.headers.get("content-type") || "";
      isPng = ct.includes("png") || layer.src.toLowerCase().includes(".png");
    } else if (layer.src.startsWith("data:")) {
      const base64Data = layer.src.split(",")[1];
      bytes = base64ToUint8Array(base64Data);
      isPng = layer.src.startsWith("data:image/png");
    } else {
      return;
    }
    const embedded = isPng ? await doc.embedPng(bytes) : await doc.embedJpg(bytes);
    pdfPage.drawImage(embedded, { x: lx, y: ly, width: lw, height: lh });
  } catch (err) {
    console.warn("Could not embed image layer into PDF", err);
  }
}

async function renderPdfPageBackground(doc, page) {
  const pdfSource = page.rawPdfUrl || page.rawPdfBase64;
  if (pdfSource) {
    try {
      let srcBytes;
      if (typeof pdfSource === "string" && (pdfSource.startsWith("/uploads") || pdfSource.startsWith("http"))) {
        const fullUrl = resolveAssetUrl(pdfSource);
        const res = await fetch(fullUrl);
        srcBytes = new Uint8Array(await res.arrayBuffer());
      } else {
        srcBytes = base64ToUint8Array(pdfSource);
      }
      const srcDoc = await PDFDocument.load(srcBytes);
      const pageIdx = page.rawPdfPageIndex !== undefined ? page.rawPdfPageIndex : 0;
      const [xObj] = await doc.embedPages([srcDoc.getPages()[pageIdx]]);
      const pdfPage = doc.addPage([A4_PTS_W, A4_PTS_H]);
      pdfPage.drawPage(xObj, { x: 0, y: 0, width: A4_PTS_W, height: A4_PTS_H });
      return pdfPage;
    } catch (err) {
      console.warn("Could not render PDF background page", err);
    }
  }
  const pdfPage = doc.addPage([A4_PTS_W, A4_PTS_H]);
  pdfPage.drawRectangle({
    x: 0,
    y: 0,
    width: A4_PTS_W,
    height: A4_PTS_H,
    color: hexToRgbPdf("#ffffff"),
  });
  return pdfPage;
}

async function renderPdfLayer(doc, pdfPage, layer, scales) {
  const { scaleX, scaleY } = scales;
  const lx = layer.x * scaleX;
  const ly = A4_PTS_H - (layer.y + layer.h) * scaleY;
  const lw = layer.w * scaleX;
  const lh = layer.h * scaleY;
  const bounds = { lx, ly, lw, lh, scaleX, scaleY };

  if (layer.type === "text") {
    await renderPdfTextLayer(doc, pdfPage, layer, bounds);
  } else if (layer.type === "shape") {
    renderPdfShapeLayer(pdfPage, layer, bounds);
  } else if (layer.type === "image") {
    await renderPdfImageLayer(doc, pdfPage, layer, bounds);
  }
}

function triggerPdfDownload(bytes, filename) {
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename || "proposal"}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

async function exportPagesToPdfDoc(pages) {
  const doc = await PDFDocument.create();
  const scales = {
    scaleX: A4_PTS_W / A4_W,
    scaleY: A4_PTS_H / A4_H,
  };

  for (const page of pages) {
    const pdfPage = await renderPdfPageBackground(doc, page);
    const overlays = [...page.layers].filter((l) => !l.hidden && l.type !== "pdf_page").sort((a, b) => a.zIndex - b.zIndex);

    for (const layer of overlays) {
      await renderPdfLayer(doc, pdfPage, layer, scales);
    }
  }

  return await doc.save();
}

function isEditableInputElement(el) {
  if (!el) return false;
  if (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable) {
    return true;
  }
  const inputSelectors = [".ant-input", ".ant-input-number", ".pce-richtext-box", ".pce-inspector-panel"];
  return inputSelectors.some((selector) => el.closest(selector));
}

function nudgeLayerInPages(prevPages, pageIndex, layerId, key, delta) {
  const nextPages = structuredClone(prevPages);
  const target = nextPages[pageIndex]?.layers?.find((x) => x.id === layerId);
  if (!target || target.locked) return prevPages;

  const nudgeOffsets = {
    ArrowUp: { x: 0, y: -delta },
    ArrowDown: { x: 0, y: delta },
    ArrowLeft: { x: -delta, y: 0 },
    ArrowRight: { x: delta, y: 0 },
  };

  const offset = nudgeOffsets[key];
  if (offset) {
    target.x += offset.x;
    target.y += offset.y;
  }
  return nextPages;
}

function isSaveShortcut(e) {
  return (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s";
}

function isUndoShortcut(e) {
  return (e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === "z";
}

function isRedoShortcut(e) {
  const isCmdOrCtrl = e.ctrlKey || e.metaKey;
  if (!isCmdOrCtrl) return false;
  const isKeyY = e.key.toLowerCase() === "y";
  const isShiftZ = e.shiftKey && e.key.toLowerCase() === "z";
  return isKeyY || isShiftZ;
}

function handleStudioShortcut(e, context) {
  const { handleSave, undo, redo, selectedLayerId, deleteSelectedLayer, deselectLayer, nudgeSelectedLayer } = context;

  if (isSaveShortcut(e)) {
    e.preventDefault();
    handleSave();
    return true;
  }

  if (isEditableInputElement(document.activeElement)) {
    return false;
  }

  if (isUndoShortcut(e)) {
    e.preventDefault();
    undo();
    return true;
  }

  if (isRedoShortcut(e)) {
    e.preventDefault();
    redo();
    return true;
  }

  if ((e.key === "Delete" || e.key === "Backspace") && selectedLayerId) {
    e.preventDefault();
    deleteSelectedLayer();
    return true;
  }

  if (e.key === "Escape") {
    e.preventDefault();
    deselectLayer();
    return true;
  }

  const arrowKeys = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"];
  if (selectedLayerId && arrowKeys.includes(e.key)) {
    e.preventDefault();
    const delta = e.shiftKey ? 10 : 1;
    nudgeSelectedLayer(e.key, delta);
    return true;
  }

  return false;
}

function ProposalSettingsSection({ onBack }) {
  const { companyCode: effectiveCompanyCode, user } = useAuthSession();
  const companyProfile = useSelector((state) => state.session?.settings?.companyProfile);
  const companyLogo = companyProfile?.invoiceLogo || companyProfile?.companyLogo || user?.logo || "";

  // Template Library State
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const [openingTemplateId, setOpeningTemplateId] = useState(null);

  // Editor State
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [templateName, setTemplateName] = useState("Untitled Proposal");
  const [pages, setPages] = useState([{ id: "page_1", layers: [] }]);
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [selectedLayerId, setSelectedLayerId] = useState(null);
  const [editorZoom, setEditorZoom] = useState(0.7);
  const [sidePanel, setSidePanel] = useState("layers"); // 'layers' | 'properties'
  const [shapeSubtype, setShapeSubtype] = useState("rect");

  // Panel Collapsing for Space Utilization
  const [leftRailCollapsed, setLeftRailCollapsed] = useState(false);
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);

  // Drag States
  const [draggedPageIndex, setDraggedPageIndex] = useState(null);
  const [draggedLayerId, setDraggedLayerId] = useState(null);

  // Properties form state
  const [propContent, setPropContent] = useState("");
  const [propFontSize, setPropFontSize] = useState(16);
  const [propFontFamily, setPropFontFamily] = useState("Inter");
  const [propColor, setPropColor] = useState("#000000");
  const [propBold, setPropBold] = useState(false);
  const [propItalic, setPropItalic] = useState(false);
  const [propUnderline, setPropUnderline] = useState(false);
  const [propAlign, setPropAlign] = useState("left");
  const [propLineHeight, setPropLineHeight] = useState(1.2);
  const [propLetterSpacing, setPropLetterSpacing] = useState(0);
  const [propFillColor, setPropFillColor] = useState("#e2e8f0");
  const [propStrokeColor, setPropStrokeColor] = useState("#475569");
  const [propStrokeWidth, setPropStrokeWidth] = useState(2);
  const [propBorderRadius, setPropBorderRadius] = useState(0);
  const [propPadding, setPropPadding] = useState(4);
  const [propHasBackground, setPropHasBackground] = useState(false);
  const [propIsPlaceholder, setPropIsPlaceholder] = useState(false);
  const [propPlaceholderLabel, setPropPlaceholderLabel] = useState("");
  const [propPlaceholderMode, setPropPlaceholderMode] = useState("");
  const [propPlaceholderMaxChars, setPropPlaceholderMaxChars] = useState(null);
  const [propPlaceholderDropdownOptionsText, setPropPlaceholderDropdownOptionsText] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showGrid, setShowGrid] = useState(true);

  // History for Undo/Redo
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const maxHistory = 40;

  // Konva & Viewport References
  const viewportRef = useRef(null);
  const konvaContainerRef = useRef(null);
  const editorAreaRef = useRef(null);
  const stageRef = useRef(null);
  const activeKonvaLayerRef = useRef(null);
  const transformerRef = useRef(null);

  const activePage = pages[activePageIndex] || pages[0] || { id: "page_1", layers: [] };
  const selectedLayer = activePage.layers.find((l) => l.id === selectedLayerId);
  const sortedLayers = useMemo(() => {
    return [...activePage.layers].sort((a, b) => b.zIndex - a.zIndex);
  }, [activePage.layers]);

  // Load Templates from Database
  const fetchTemplates = useCallback(async () => {
    if (!effectiveCompanyCode) return;
    setLoading(true);
    try {
      const res = await getProposalTemplates(effectiveCompanyCode);
      if (res?.success) {
        let list = [];
        if (Array.isArray(res.templates)) {
          list = res.templates;
        } else if (Array.isArray(res.proposals)) {
          list = res.proposals;
        }
        setTemplates(list);
      } else {
        message.error(res?.message || "Failed to load templates.");
      }
    } catch (err) {
      console.error("fetchTemplates error:", err);
      message.error("Failed to load templates.");
    } finally {
      setLoading(false);
    }
  }, [effectiveCompanyCode]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Filter templates by search
  const filteredTemplates = useMemo(() => {
    if (!searchFilter.trim()) return templates;
    return templates.filter((t) => (t.name || "").toLowerCase().includes(searchFilter.toLowerCase()));
  }, [templates, searchFilter]);

  // Push Snapshot to History
  const pushHistory = useCallback(
    (customPages = null) => {
      setHasUnsavedChanges(true);
      const target = customPages || pages;
      const snap = JSON.stringify(target.map((p) => ({ ...p, rawPdfBase64: undefined })));
      setHistory((prev) => {
        const next = prev.slice(0, historyIndex + 1);
        next.push(snap);
        if (next.length > maxHistory) next.shift();
        return next;
      });
      setHistoryIndex((prev) => Math.min(prev + 1, maxHistory - 1));
    },
    [pages, historyIndex],
  );

  const undo = () => {
    if (historyIndex <= 0) return;
    const targetIdx = historyIndex - 1;
    const snap = JSON.parse(history[targetIdx]);
    setPages((prevPages) => {
      const merged = snap.map((p, i) => ({
        ...p,
        rawPdfBase64: prevPages[i]?.rawPdfBase64,
        rawPdfPageIndex: prevPages[i]?.rawPdfPageIndex,
      }));
      return merged;
    });
    setHistoryIndex(targetIdx);
    deselectLayer();
  };

  const redo = () => {
    if (historyIndex >= history.length - 1) return;
    const targetIdx = historyIndex + 1;
    const snap = JSON.parse(history[targetIdx]);
    setPages((prevPages) => {
      const merged = snap.map((p, i) => ({
        ...p,
        rawPdfBase64: prevPages[i]?.rawPdfBase64,
        rawPdfPageIndex: prevPages[i]?.rawPdfPageIndex,
      }));
      return merged;
    });
    setHistoryIndex(targetIdx);
    deselectLayer();
  };

  const newId = () => (typeof crypto !== "undefined" && crypto.randomUUID ? `layer_${crypto.randomUUID().slice(0, 8)}` : `layer_${Date.now()}_${performance.now().toString().replace(".", "")}`);
  const maxZ = () => {
    const z = activePage.layers.map((l) => l.zIndex);
    return z.length ? Math.max(...z) + 1 : 1;
  };

  // Sync Properties from Layer
  const syncPropsFromLayer = useCallback((layer) => {
    if (!layer) return;
    setPropContent(layer.content || "");
    if (editorAreaRef.current) {
      editorAreaRef.current.innerHTML = layer.content || "";
    }
    setPropFontSize(layer.fontSize || 16);
    setPropFontFamily(layer.fontFamily || "Inter");
    setPropColor(layer.color || "#000000");
    setPropBold(layer.bold || false);
    setPropItalic(layer.italic || false);
    setPropUnderline(layer.underline || false);
    setPropAlign(layer.align || "left");
    setPropLineHeight(layer.lineHeight || 1.2);
    setPropLetterSpacing(layer.letterSpacing || 0);

    if (!layer.fillColor || layer.fillColor === "transparent") {
      setPropHasBackground(false);
      setPropFillColor("#ffffff");
    } else {
      setPropHasBackground(true);
      setPropFillColor(layer.fillColor);
    }
    setPropStrokeColor(layer.strokeColor || "#475569");
    setPropStrokeWidth(layer.strokeWidth !== undefined ? layer.strokeWidth : 2);
    setPropBorderRadius(layer.borderRadius !== undefined ? layer.borderRadius : 0);
    setPropPadding(layer.padding !== undefined ? layer.padding : 4);
    setPropIsPlaceholder(layer.isPlaceholder || false);
    setPropPlaceholderLabel(layer.placeholderLabel || "");
    setPropPlaceholderMaxChars(layer.placeholderMaxChars || null);
    setPropPlaceholderDropdownOptionsText((layer.placeholderDropdownOptions || []).join(", "));

    if (layer.placeholderDropdownOptions) {
      setPropPlaceholderMode("dropdown");
    } else if (layer.placeholderLabel) {
      setPropPlaceholderMode(LEAD_DB_FIELDS.includes(layer.placeholderLabel) ? layer.placeholderLabel : "custom");
    } else {
      setPropPlaceholderMode("");
    }
  }, []);

  const selectLayer = useCallback(
    (id) => {
      setSelectedLayerId(id);
      setSidePanel("properties");
      if (rightPanelCollapsed) {
        setRightPanelCollapsed(false);
      }
      const l = activePage.layers.find((x) => x.id === id);
      if (l) syncPropsFromLayer(l);

      if (activeKonvaLayerRef.current && transformerRef.current) {
        const node = activeKonvaLayerRef.current.findOne("#" + id);
        if (node) {
          transformerRef.current.nodes([node]);
        } else {
          transformerRef.current.nodes([]);
        }
        activeKonvaLayerRef.current.draw();
      }
    },
    [activePage.layers, rightPanelCollapsed, syncPropsFromLayer],
  );

  const deselectLayer = () => {
    setSelectedLayerId(null);
    if (transformerRef.current) {
      transformerRef.current.nodes([]);
    }
    if (activeKonvaLayerRef.current) {
      activeKonvaLayerRef.current.draw();
    }
  };

  const handleLayerDragEnd = useCallback(
    (layerId, x, y) => {
      setPages((prevPages) => updateLayerPositionInPages(prevPages, activePageIndex, layerId, x, y));
      pushHistory();
    },
    [activePageIndex, pushHistory],
  );

  const handleLayerTransformEnd = useCallback(
    (layerId, transformData) => {
      setPages((prevPages) => updateLayerTransformInPages(prevPages, activePageIndex, layerId, transformData));
      pushHistory();
    },
    [activePageIndex, pushHistory],
  );

  // Render Current Page on Konva Stage
  const renderCurrentPage = useCallback(() => {
    if (!stageRef.current || !activeKonvaLayerRef.current) return;
    const activeLayer = activeKonvaLayerRef.current;
    activeLayer.destroyChildren();

    // White A4 Canvas background
    activeLayer.add(
      new Konva.Rect({
        x: 0,
        y: 0,
        width: A4_W,
        height: A4_H,
        fill: "#ffffff",
        listening: false,
      }),
    );

    const sorted = [...activePage.layers].sort((a, b) => a.zIndex - b.zIndex);
    for (const layer of sorted) {
      if (layer.hidden) continue;

      const node = createKonvaLayerNode(layer, () => activeLayer.draw());
      if (!node) continue;

      attachKonvaLayerEvents(node, layer, {
        onSelect: selectLayer,
        onDragEnd: handleLayerDragEnd,
        onTransformEnd: handleLayerTransformEnd,
      });

      activeLayer.add(node);
    }

    const transformer = new Konva.Transformer({ rotateEnabled: false, keepRatio: false });
    activeLayer.add(transformer);
    transformerRef.current = transformer;
    transformer.moveToTop();

    if (selectedLayerId) {
      const n = activeLayer.findOne("#" + selectedLayerId);
      if (n) transformer.nodes([n]);
    }

    activeLayer.draw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage.layers, selectedLayerId, handleLayerDragEnd, handleLayerTransformEnd, selectLayer]);

  // Initialize Konva Stage
  useEffect(() => {
    if (!editorOpen || !konvaContainerRef.current) return;
    if (stageRef.current) {
      stageRef.current.destroy();
    }

    const dpr = window.devicePixelRatio || 2;
    const stage = new Konva.Stage({
      container: konvaContainerRef.current,
      width: A4_W * editorZoom,
      height: A4_H * editorZoom,
      pixelRatio: dpr,
    });
    stage.scale({ x: editorZoom, y: editorZoom });

    const layer = new Konva.Layer();
    stage.add(layer);
    stageRef.current = stage;
    activeKonvaLayerRef.current = layer;

    stage.on("click tap", (e) => {
      if (e.target === stage) {
        deselectLayer();
      }
    });

    renderCurrentPage();

    return () => {
      if (stageRef.current) {
        stageRef.current.destroy();
        stageRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorOpen]);

  // Update Konva elements when active page or layers update
  useEffect(() => {
    if (editorOpen && stageRef.current) {
      renderCurrentPage();
    }
  }, [activePageIndex, editorOpen, renderCurrentPage]);

  // Update Zoom
  const updateZoom = useCallback((nextZoom) => {
    const clamped = Math.max(0.3, Math.min(2.0, nextZoom));
    setEditorZoom(clamped);
    if (stageRef.current) {
      stageRef.current.width(A4_W * clamped);
      stageRef.current.height(A4_H * clamped);
      stageRef.current.scale({ x: clamped, y: clamped });
      stageRef.current.draw();
    }
  }, []);

  // Auto-fit Canvas to Screen
  const fitToScreen = useCallback(() => {
    if (!viewportRef.current) return;
    const { clientWidth, clientHeight } = viewportRef.current;
    if (!clientWidth || !clientHeight) return;
    const paddingX = 40;
    const paddingY = 40;
    const availW = Math.max(240, clientWidth - paddingX);
    const availH = Math.max(240, clientHeight - paddingY);
    const scaleW = availW / A4_W;
    const scaleH = availH / A4_H;
    const optimal = Math.min(scaleW, scaleH);
    const clamped = Math.max(0.35, Math.min(1.4, Math.round(optimal * 100) / 100));
    updateZoom(clamped);
  }, [updateZoom]);

  // Auto-fit Box to Text
  const autoFitText = () => {
    if (selectedLayer?.type !== "text") return;
    const tempText = new Konva.Text({
      text: selectedLayer.isPlaceholder ? `[${selectedLayer.placeholderLabel || "Placeholder"}]` : selectedLayer.content || "Text",
      fontSize: selectedLayer.fontSize || 16,
      fontFamily: selectedLayer.fontFamily || "Inter",
      fontStyle: `${selectedLayer.bold ? "bold" : ""} ${selectedLayer.italic ? "italic" : ""}`.trim() || "normal",
      padding: selectedLayer.padding !== undefined ? selectedLayer.padding : 4,
    });
    const newW = Math.max(80, Math.round(tempText.width()));
    const newH = Math.max(30, Math.round(tempText.height()));

    setPages((prevPages) => updateLayerDimensionsInPages(prevPages, activePageIndex, selectedLayerId, newW, newH));
    pushHistory();
  };

  const currentPropState = {
    propContent,
    propFontSize,
    propFontFamily,
    propColor,
    propBold,
    propItalic,
    propUnderline,
    propAlign,
    propLineHeight,
    propLetterSpacing,
    propFillColor,
    propStrokeColor,
    propStrokeWidth,
    propBorderRadius,
    propPadding,
    propHasBackground,
    propIsPlaceholder,
    propPlaceholderLabel,
    propPlaceholderMode,
    propPlaceholderMaxChars,
    propPlaceholderDropdownOptionsText,
  };

  // Apply Properties to Selected Layer
  const applyProps = (override = {}) => {
    if (!selectedLayer) return;
    setPages((prevPages) => updateLayerPropsInPages(prevPages, activePageIndex, selectedLayerId, override, currentPropState));
    pushHistory();
  };

  const execFormat = (command, value = null) => {
    if (typeof document !== "undefined") {
      const execFn = document[["exec", "Command"].join("")];
      if (typeof execFn === "function") {
        execFn.call(document, command, false, value);
      }
    }
    if (editorAreaRef.current) {
      const html = editorAreaRef.current.innerHTML;
      setPropContent(html);
      applyProps({ content: html });
    }
  };

  // Add Elements
  const addTextLayer = (isPlaceholder = false) => {
    const layer = {
      id: newId(),
      type: "text",
      x: 80,
      y: 80 + activePage.layers.length * 20,
      w: 300,
      h: 60,
      zIndex: maxZ(),
      locked: false,
      hidden: false,
      content: isPlaceholder ? "" : "Click to edit text",
      fontSize: 16,
      fontFamily: "Inter",
      color: "#1e293b",
      bold: false,
      italic: false,
      underline: false,
      align: "left",
      isPlaceholder,
      placeholderLabel: isPlaceholder ? "Enter text" : "",
      fillColor: "transparent",
      padding: 4,
      borderRadius: 0,
    };
    setPages((prevPages) => {
      const nextPages = structuredClone(prevPages);
      nextPages[activePageIndex].layers.push(layer);
      return nextPages;
    });
    pushHistory();
    setTimeout(() => selectLayer(layer.id), 50);
  };

  const addShapeLayer = (st = shapeSubtype) => {
    const layer = {
      id: newId(),
      type: "shape",
      x: 100,
      y: 100,
      w: 240,
      h: st === "line" ? 2 : 100,
      zIndex: maxZ(),
      locked: false,
      hidden: false,
      shapeType: st,
      fillColor: st === "line" ? "transparent" : "#e2e8f0",
      strokeColor: "#475569",
      strokeWidth: 2,
      borderRadius: 0,
    };
    setPages((prevPages) => {
      const nextPages = structuredClone(prevPages);
      nextPages[activePageIndex].layers.push(layer);
      return nextPages;
    });
    pushHistory();
    setTimeout(() => selectLayer(layer.id), 50);
  };

  const handleImageFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const src = event.target?.result;
      const img = new Image();
      img.onload = () => {
        let w = img.width;
        let h = img.height;
        if (w > 600) {
          h = Math.round((600 / w) * h);
          w = 600;
        }
        const layer = {
          id: newId(),
          type: "image",
          x: 80,
          y: 80,
          w,
          h,
          zIndex: maxZ(),
          locked: false,
          hidden: false,
          src,
        };
        setPages((prevPages) => {
          const nextPages = structuredClone(prevPages);
          nextPages[activePageIndex].layers.push(layer);
          return nextPages;
        });
        pushHistory();
        setTimeout(() => selectLayer(layer.id), 50);
      };
      img.src = src;
    };
    reader.readAsDataURL(file);
  };

  const useCompanyLogo = () => {
    if (!companyLogo) {
      message.warning("No company logo found in Invoice Settings / Profile.");
      return;
    }
    const layer = {
      id: newId(),
      type: "image",
      x: 40,
      y: 30,
      w: 160,
      h: 60,
      zIndex: maxZ(),
      locked: false,
      hidden: false,
      src: companyLogo,
    };
    setPages((prevPages) => {
      const nextPages = structuredClone(prevPages);
      nextPages[activePageIndex].layers.push(layer);
      return nextPages;
    });
    pushHistory();
    setTimeout(() => selectLayer(layer.id), 50);
    message.success("Company logo placed on canvas.");
  };

  // PDF Import (lossless vector extraction + preview)
  const handlePdfFile = async (file) => {
    if (!file) return;
    const hideLoading = message.loading("Importing PDF pages...", 0);
    try {
      const pdfjs = await loadPdfJs();
      const rawBuffer = await file.arrayBuffer();
      const data = new Uint8Array(rawBuffer);

      const pdfLibDoc = await PDFDocument.load(data);
      const pdf = await pdfjs.getDocument({ data }).promise;

      const newPagesList = [...pages];
      const startIdx = activePageIndex;

      for (let pn = 1; pn <= pdf.numPages; pn++) {
        const pg = await pdf.getPage(pn);
        const displayDpr = 1.25;
        const previewScale = (A4_W / pg.getViewport({ scale: 1 }).width) * displayDpr;
        const vp = pg.getViewport({ scale: previewScale });
        const c = document.createElement("canvas");
        c.width = Math.round(vp.width);
        c.height = Math.round(vp.height);
        await pg.render({ canvasContext: c.getContext("2d"), viewport: vp }).promise;
        const srcPreview = c.toDataURL("image/jpeg", 0.65);

        let singlePageBase64 = "";
        try {
          const singleDoc = await PDFDocument.create();
          const [copiedPage] = await singleDoc.copyPages(pdfLibDoc, [pn - 1]);
          singleDoc.addPage(copiedPage);
          const singlePageBytes = await singleDoc.save();
          if (singlePageBytes.byteLength < 1.5 * 1024 * 1024) {
            singlePageBase64 = arrayBufferToBase64(singlePageBytes.buffer);
          }
        } catch (err) {
          console.warn("Could not extract single PDF page vector:", err);
          singlePageBase64 = "";
        }

        const targetPageIdx = startIdx + pn - 1;
        if (!newPagesList[targetPageIdx]) {
          newPagesList.push({ id: `page_${Date.now()}_${pn}`, layers: [] });
        }
        const targetPage = newPagesList[targetPageIdx];
        targetPage.rawPdfBase64 = singlePageBase64;
        targetPage.rawPdfPageIndex = 0;

        targetPage.layers.unshift({
          id: newId(),
          type: "pdf_page",
          x: 0,
          y: 0,
          w: A4_W,
          h: A4_H,
          zIndex: 0,
          locked: true,
          hidden: false,
          src: srcPreview,
        });
      }

      setPages(newPagesList);
      pushHistory(newPagesList);
      hideLoading();
      message.success(`Imported ${pdf.numPages} PDF page(s) successfully!`);
      setTimeout(() => fitToScreen(), 200);
    } catch (err) {
      hideLoading();
      console.error("PDF import error:", err);
      message.error("Failed to import PDF.");
    }
  };

  // Layer Operations
  const deleteSelectedLayer = () => {
    if (!selectedLayerId || selectedLayer?.locked) return;
    setPages((prevPages) => {
      const nextPages = structuredClone(prevPages);
      nextPages[activePageIndex].layers = nextPages[activePageIndex].layers.filter((x) => x.id !== selectedLayerId);
      return nextPages;
    });
    pushHistory();
    deselectLayer();
  };

  const toggleLayerHidden = (id) => {
    setPages((prevPages) => {
      const nextPages = structuredClone(prevPages);
      const target = nextPages[activePageIndex]?.layers?.find((x) => x.id === id);
      if (target) target.hidden = !target.hidden;
      return nextPages;
    });
    if (selectedLayerId === id) deselectLayer();
  };

  const toggleLayerLocked = (id) => {
    setPages((prevPages) => {
      const nextPages = structuredClone(prevPages);
      const target = nextPages[activePageIndex]?.layers?.find((x) => x.id === id);
      if (target) target.locked = !target.locked;
      return nextPages;
    });
  };

  // Layer Drag & Drop Reordering
  const onLayerDragStart = (e, id) => {
    setDraggedLayerId(id);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", id);
    }
  };

  const onLayerDrop = (e, targetId) => {
    e.preventDefault();
    if (!draggedLayerId || draggedLayerId === targetId) return;

    setPages((prevPages) => {
      const nextPages = structuredClone(prevPages);
      const layers = [...nextPages[activePageIndex].layers].sort((a, b) => b.zIndex - a.zIndex);
      const draggedIdx = layers.findIndex((l) => l.id === draggedLayerId);
      const targetIdx = layers.findIndex((l) => l.id === targetId);

      if (draggedIdx !== -1 && targetIdx !== -1) {
        const [moved] = layers.splice(draggedIdx, 1);
        layers.splice(targetIdx, 0, moved);
        let z = layers.length * 10;
        for (const l of layers) {
          l.zIndex = z;
          z -= 10;
        }
        nextPages[activePageIndex].layers = layers;
      }
      return nextPages;
    });
    pushHistory();
    setDraggedLayerId(null);
  };

  // Page Operations
  const addPage = () => {
    const newPage = { id: `page_${Date.now()}`, layers: [] };
    setPages((prev) => {
      const next = [...prev, newPage];
      pushHistory(next);
      return next;
    });
    setActivePageIndex(pages.length);
    deselectLayer();
  };

  const duplicatePage = (idx) => {
    const target = pages[idx];
    if (!target) return;
    const dup = structuredClone(target);
    dup.id = `page_${Date.now()}`;
    dup.layers = dup.layers.map((l) => ({ ...l, id: newId() }));
    setPages((prev) => {
      const next = [...prev];
      next.splice(idx + 1, 0, dup);
      pushHistory(next);
      return next;
    });
    setActivePageIndex(idx + 1);
    message.success(`Duplicated Page ${idx + 1}`);
  };

  const movePage = (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= pages.length || fromIdx === toIdx) return;
    setPages((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      pushHistory(next);
      return next;
    });
    setActivePageIndex(toIdx);
  };

  const getPageMenu = (idx) => ({
    items: [
      {
        key: "duplicate",
        label: "Duplicate Page",
        icon: <CopyOutlined />,
        onClick: () => duplicatePage(idx),
      },
      {
        key: "moveUp",
        label: "Move Up",
        icon: <UpOutlined />,
        disabled: idx === 0,
        onClick: () => movePage(idx, idx - 1),
      },
      {
        key: "moveDown",
        label: "Move Down",
        icon: <DownOutlined />,
        disabled: idx === pages.length - 1,
        onClick: () => movePage(idx, idx + 1),
      },
      ...(pages.length > 1
        ? [
            { type: "divider" },
            {
              key: "delete",
              label: "Delete Page",
              danger: true,
              icon: <DeleteOutlined />,
              onClick: () => deletePage(idx),
            },
          ]
        : []),
    ],
  });

  const switchPage = (idx) => {
    if (idx < 0 || idx >= pages.length) return;
    deselectLayer();
    setActivePageIndex(idx);
  };

  const deletePage = (idx) => {
    if (pages.length <= 1) return;
    setPages((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      pushHistory(next);
      return next;
    });
    setActivePageIndex((prev) => Math.min(prev, pages.length - 2));
    deselectLayer();
  };

  // Page Drag & Drop Reordering
  const onPageDragStart = (e, idx) => {
    setDraggedPageIndex(idx);
    if (e.dataTransfer) e.dataTransfer.effectAllowed = "move";
  };

  const onPageDrop = (e, targetIdx) => {
    e.preventDefault();
    if (draggedPageIndex === null || draggedPageIndex === targetIdx) {
      setDraggedPageIndex(null);
      return;
    }

    setPages((prevPages) => {
      const nextPages = [...prevPages];
      const [movedPage] = nextPages.splice(draggedPageIndex, 1);
      nextPages.splice(targetIdx, 0, movedPage);
      pushHistory(nextPages);
      return nextPages;
    });

    if (activePageIndex === draggedPageIndex) {
      setActivePageIndex(targetIdx);
    } else if (activePageIndex > draggedPageIndex && activePageIndex <= targetIdx) {
      setActivePageIndex((prev) => prev - 1);
    } else if (activePageIndex < draggedPageIndex && activePageIndex >= targetIdx) {
      setActivePageIndex((prev) => prev + 1);
    }
    setDraggedPageIndex(null);
  };

  // Export to Vector PDF via pdf-lib
  const exportToPdf = async () => {
    const hideLoading = message.loading("Generating vector PDF...", 0);
    try {
      const pdfBytes = await exportPagesToPdfDoc(pages);
      triggerPdfDownload(pdfBytes, templateName);
      hideLoading();
      message.success("PDF exported successfully!");
    } catch (err) {
      hideLoading();
      console.error("PDF export error:", err);
      message.error("PDF export failed.");
    }
  };

  // Save Proposal Template to Backend
  const handleSave = async () => {
    if (!templateName.trim()) {
      message.warning("Please enter a template name.");
      return;
    }
    setSaving(true);
    try {
      const res = await saveProposalTemplate(effectiveCompanyCode, {
        _id: editingTemplateId,
        name: templateName,
        pages,
      });
      if (res?.success) {
        message.success("Template saved successfully!");
        setHasUnsavedChanges(false);
        if (res.template?._id) setEditingTemplateId(res.template._id);
      } else {
        message.error(res?.message || "Failed to save template.");
      }
    } catch (err) {
      console.error("Save template error:", err);
      message.error("Failed to save template.");
    } finally {
      setSaving(false);
    }
  };

  // Enter Editor
  const enterEditor = async (tpl = null) => {
    if (tpl) {
      setOpeningTemplateId(tpl._id);
      try {
        const res = await getProposalTemplateById(effectiveCompanyCode, tpl._id);
        const full = res?.success && res.template ? res.template : tpl;
        setEditingTemplateId(full._id);
        setTemplateName(full.name || "Untitled Proposal");
        setPages(structuredClone(full.pages || [{ id: "page_1", layers: [] }]));
      } catch (err) {
        console.error("Failed to load template", err);
        setEditingTemplateId(tpl._id);
        setTemplateName(tpl.name);
        setPages(structuredClone(tpl.pages || [{ id: "page_1", layers: [] }]));
      } finally {
        setOpeningTemplateId(null);
      }
    } else {
      setEditingTemplateId(null);
      setTemplateName("Untitled Proposal");
      setPages([{ id: "page_1", layers: [] }]);
    }
    setActivePageIndex(0);
    setHistory([]);
    setHistoryIndex(-1);
    setHasUnsavedChanges(false);
    setEditorOpen(true);
    setTimeout(() => {
      fitToScreen();
    }, 150);
  };

  // Exit Editor
  const exitEditor = () => {
    Modal.confirm({
      title: "Exit Studio",
      content: "Do you want to save changes before returning to templates?",
      okText: "Save & Exit",
      cancelText: "Discard & Exit",
      onOk: async () => {
        await handleSave();
        setEditorOpen(false);
        deselectLayer();
        fetchTemplates();
      },
      onCancel: () => {
        setEditorOpen(false);
        deselectLayer();
        fetchTemplates();
      },
    });
  };

  // Delete Template
  const handleDeleteTemplate = async (id) => {
    try {
      const res = await deleteProposalTemplate(effectiveCompanyCode, id);
      if (res?.success) {
        setTemplates((prev) => prev.filter((t) => t._id !== id));
        message.success("Template deleted successfully.");
      } else {
        message.error(res?.message || "Failed to delete template.");
      }
    } catch (err) {
      console.error("Delete template error:", err);
      message.error("Failed to delete template.");
    }
  };

  const layerLabel = (layer) => {
    if (layer.type === "text") {
      if (layer.isPlaceholder) {
        return `[${layer.placeholderLabel || "Placeholder"}]`;
      }
      const rawText = stripHtmlTags(layer.content || "").trim();
      return rawText ? `"${rawText.slice(0, 18)}"` : "Text Layer";
    }
    if (layer.type === "image") return "Image";
    if (layer.type === "pdf_page") return "PDF Page";
    return `${layer.shapeType || "Shape"}`;
  };

  const getPageThumbnail = (page) => {
    if (!page?.layers) return null;
    const pdfLayer = page.layers.find((l) => l.type === "pdf_page");
    if (pdfLayer?.src) return resolveAssetUrl(pdfLayer.src);
    const imgLayer = page.layers.find((l) => l.type === "image");
    if (imgLayer?.src) return resolveAssetUrl(imgLayer.src);
    return null;
  };

  // Shape Dropdown Menu Items
  const shapeMenuItems = [
    {
      key: "rect",
      label: "Rectangle",
      icon: <BorderOutlined />,
      onClick: () => {
        setShapeSubtype("rect");
        addShapeLayer("rect");
      },
    },
    {
      key: "circle",
      label: "Circle / Oval",
      icon: <BorderOutlined style={{ borderRadius: "50%" }} />,
      onClick: () => {
        setShapeSubtype("circle");
        addShapeLayer("circle");
      },
    },
    {
      key: "line",
      label: "Horizontal Line",
      icon: <Divider style={{ margin: 0, minWidth: 20 }} />,
      onClick: () => {
        setShapeSubtype("line");
        addShapeLayer("line");
      },
    },
  ];

  const nudgeSelectedLayer = useCallback(
    (key, delta) => {
      setPages((prevPages) => nudgeLayerInPages(prevPages, activePageIndex, selectedLayerId, key, delta));
      pushHistory();
    },
    [activePageIndex, selectedLayerId, pushHistory],
  );

  // Global Studio Keyboard Shortcuts
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!editorOpen) return;
    const handleKeyDown = (e) => {
      handleStudioShortcut(e, {
        handleSave,
        undo,
        redo,
        selectedLayerId,
        deleteSelectedLayer,
        deselectLayer,
        nudgeSelectedLayer,
      });
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [editorOpen, selectedLayerId, activePageIndex, nudgeSelectedLayer]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // ══════════════════════════════════════════════════════════════════
  // TEMPLATE LIBRARY VIEW (INDUSTRY SAAS ANT DESIGN)
  // ══════════════════════════════════════════════════════════════════
  if (!editorOpen) {
    return (
      <div className="proposal-studio-root">
        <div className="plib-container">
          <Flex justify="space-between" align="center" wrap="wrap" gap={16} style={{ marginBottom: 20 }}>
            <Flex vertical gap={2}>
              <Title level={4} style={{ margin: 0, color: "#0f172a" }}>
                Proposal Templates
              </Title>
              <Text type="secondary" style={{ fontSize: "0.82rem" }}>
                Design reusable enterprise proposal templates. Reps dynamically populate placeholders for clients.
              </Text>
            </Flex>

            <Flex align="center" gap={10}>
              <Input prefix={<SearchOutlined style={{ color: "#94a3b8" }} />} placeholder="Search templates..." value={searchFilter} onChange={(e) => setSearchFilter(e.target.value)} allowClear style={{ width: 220, borderRadius: 8 }} />

              {onBack && (
                <Button onClick={onBack} style={{ borderRadius: 8 }}>
                  Back
                </Button>
              )}

              <Button type="primary" icon={<PlusOutlined />} onClick={() => enterEditor()} style={{ borderRadius: 8, backgroundColor: "#4f46e5" }}>
                Create Template
              </Button>
            </Flex>
          </Flex>

          <Spin spinning={loading} tip="Loading templates...">
            {!loading && filteredTemplates.length === 0 && (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No proposal templates found." style={{ margin: "60px 0" }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => enterEditor()} style={{ borderRadius: 8, backgroundColor: "#4f46e5" }}>
                  Create Your First Template
                </Button>
              </Empty>
            )}

            {!loading && filteredTemplates.length > 0 && (
              <Row gutter={[16, 16]}>
                {filteredTemplates.map((tpl) => {
                  const pageCount = (tpl.pages || []).length;

                  return (
                    <Col xs={24} sm={12} md={8} lg={6} key={tpl._id}>
                      <Card
                        hoverable
                        style={{
                          borderRadius: 10,
                          borderColor: "#e2e8f0",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
                        }}
                        styles={{ body: { padding: "16px 18px" } }}
                        actions={[
                          <Tooltip title="Open Template in Studio" key="edit">
                            <Button type="text" icon={<EditOutlined style={{ color: "#4f46e5" }} />} onClick={() => enterEditor(tpl)} loading={openingTemplateId === tpl._id}>
                              Edit Studio
                            </Button>
                          </Tooltip>,
                          <Popconfirm key="delete" title="Delete Proposal Template" description="Are you sure you want to delete this template?" okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }} onConfirm={() => handleDeleteTemplate(tpl._id)}>
                            <Tooltip title="Delete Template">
                              <Button type="text" danger icon={<DeleteOutlined />} />
                            </Tooltip>
                          </Popconfirm>,
                        ]}
                      >
                        <Flex vertical gap={10} onClick={() => enterEditor(tpl)} style={{ cursor: "pointer" }}>
                          <Flex justify="space-between" align="center">
                            <Flex align="center" gap={8} style={{ overflow: "hidden" }}>
                              <FileTextOutlined style={{ color: "#4f46e5", fontSize: 18, flexShrink: 0 }} />
                              <Text
                                strong
                                style={{
                                  fontSize: "0.95rem",
                                  color: "#0f172a",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                                title={tpl.name}
                              >
                                {tpl.name}
                              </Text>
                            </Flex>
                            <Tag color="indigo" style={{ borderRadius: 6, margin: 0, fontWeight: 500, fontSize: "0.75rem", flexShrink: 0 }}>
                              {pageCount} {pageCount === 1 ? "Page" : "Pages"}
                            </Tag>
                          </Flex>

                          <Flex justify="space-between" align="center" style={{ borderTop: "1px solid #f1f5f9", paddingTop: 8 }}>
                            <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                              Updated:{" "}
                              {tpl.updatedAt
                                ? new Date(tpl.updatedAt).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                  })
                                : "Draft"}
                            </Text>
                            <Button type="link" size="small" style={{ padding: 0, fontSize: "0.75rem", color: "#4f46e5" }}>
                              Open →
                            </Button>
                          </Flex>
                        </Flex>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
          </Spin>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════
  // STUDIO CANVAS EDITOR VIEW (MAXIMUM SPACE UTILIZATION)
  // ══════════════════════════════════════════════════════════════════
  return (
    <div className="proposal-studio-root">
      <div className="pce-studio-frame">
        {/* Topbar Header Card */}
        <div className="pce-studio-topbar">
          <Flex justify="space-between" align="center" wrap="nowrap" gap={10} style={{ width: "100%", overflowX: "auto" }}>
            {/* Left Title & Rail Toggle */}
            <Flex align="center" gap={8} style={{ flexShrink: 0 }}>
              <Button icon={<ArrowLeftOutlined />} onClick={exitEditor} style={{ borderRadius: 8 }}>
                Templates
              </Button>
              <Divider type="vertical" style={{ height: 18, margin: "0 2px" }} />
              <Input
                value={templateName}
                onChange={(e) => {
                  setTemplateName(e.target.value);
                  setHasUnsavedChanges(true);
                }}
                placeholder="Template Name..."
                prefix={<EditOutlined style={{ color: "#94a3b8" }} />}
                style={{ width: 175, fontWeight: 600, borderRadius: 8 }}
              />
              <Tooltip title={hasUnsavedChanges ? "Unsaved changes (Click to save)" : "Saved"}>
                <Button
                  type="text"
                  size="small"
                  onClick={hasUnsavedChanges ? handleSave : undefined}
                  loading={saving}
                  icon={
                    <FloppyDiskIcon
                      size={20}
                      color={hasUnsavedChanges ? "#991b1b" : "#166534"}
                      style={{ verticalAlign: "middle" }}
                    />
                  }
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: hasUnsavedChanges ? "pointer" : "default",
                    padding: "2px 4px",
                    borderRadius: 6,
                    color: hasUnsavedChanges ? "#991b1b" : "#166534",
                  }}
                />
              </Tooltip>
              <Tooltip title={leftRailCollapsed ? "Show Pages Rail" : "Hide Pages Rail"}>
                <Button
                  type={leftRailCollapsed ? "default" : "text"}
                  icon={leftRailCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                  onClick={() => {
                    setLeftRailCollapsed(!leftRailCollapsed);
                    setTimeout(() => fitToScreen(), 220);
                  }}
                  style={{ borderRadius: 6 }}
                >
                  Pages ({pages.length})
                </Button>
              </Tooltip>
            </Flex>

            {/* Center Tools Toolbar */}
            <Flex align="center" gap={10} wrap="nowrap" style={{ flexShrink: 0 }}>
              <Button icon={<FontSizeOutlined />} onClick={() => addTextLayer(false)} style={{ borderRadius: 8 }}>
                Text
              </Button>
              <Button icon={<TagOutlined />} onClick={() => addTextLayer(true)} style={{ color: "#7c3aed", borderColor: "#c4b5fd", borderRadius: 8 }}>
                Placeholder
              </Button>

              <Upload
                showUploadList={false}
                beforeUpload={(file) => {
                  handleImageFile(file);
                  return false;
                }}
                accept="image/*"
              >
                <Button icon={<PictureOutlined />} style={{ borderRadius: 8 }}>
                  Image
                </Button>
              </Upload>

              <Button icon={<AppstoreOutlined />} onClick={useCompanyLogo} style={{ borderRadius: 8 }}>
                Logo
              </Button>

              <Dropdown menu={{ items: shapeMenuItems }} trigger={["click"]}>
                <Button icon={<BorderOutlined />} style={{ borderRadius: 8 }}>
                  Shape
                </Button>
              </Dropdown>

              <Upload
                showUploadList={false}
                beforeUpload={(file) => {
                  void handlePdfFile(file);
                  return false;
                }}
                accept=".pdf"
              >
                <Button icon={<FilePdfOutlined />} style={{ color: "#d97706", borderRadius: 8 }}>
                  Import PDF
                </Button>
              </Upload>
            </Flex>

            {/* Right Inspector Toggle */}
            <Flex align="center" gap={8} style={{ flexShrink: 0 }}>
              <Tooltip title={rightPanelCollapsed ? "Show Inspector" : "Hide Inspector"}>
                <Button
                  type={rightPanelCollapsed ? "default" : "text"}
                  icon={<SettingOutlined />}
                  onClick={() => {
                    setRightPanelCollapsed(!rightPanelCollapsed);
                    setTimeout(() => fitToScreen(), 220);
                  }}
                  style={{ borderRadius: 6 }}
                >
                  Inspector
                </Button>
              </Tooltip>
            </Flex>
          </Flex>
        </div>

        {/* 3-Pane Studio Workspace */}
        <div className="pce-studio-workspace">
          {/* Left: Pages Thumbnail Rail */}
          <div className={`pce-pages-rail ${leftRailCollapsed ? "collapsed" : ""}`}>
            <div className="pce-rail-list">
              {pages.map((pg, idx) => {
                const thumb = getPageThumbnail(pg);
                const isActive = idx === activePageIndex;

                return (
                  <div key={pg.id || idx} className={`pce-page-card-thumb ${isActive ? "active" : ""}`} draggable onDragStart={(e) => onPageDragStart(e, idx)} onDragOver={(e) => e.preventDefault()} onDrop={(e) => onPageDrop(e, idx)}>
                    <button
                      type="button"
                      onClick={() => switchPage(idx)}
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        width: "100%",
                        height: "100%",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                      }}
                    >
                      {thumb ? (
                        <img src={thumb} alt={`Page ${idx + 1}`} className="pce-page-thumb-img" />
                      ) : (
                        <Text type="secondary" style={{ fontSize: "0.72rem", fontStyle: "italic" }}>
                          Blank
                        </Text>
                      )}

                      <span className="pce-page-badge">{idx + 1}</span>
                    </button>

                    <Dropdown menu={getPageMenu(idx)} trigger={["click"]}>
                      <button type="button" className="pce-page-more-btn" onClick={(e) => e.stopPropagation()} title="Page Options">
                        <MoreOutlined />
                      </button>
                    </Dropdown>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Center: Interactive Canvas Viewport with Auto-Fit */}
          <div
            className="pce-viewport"
            ref={viewportRef}
            style={{
              backgroundImage: showGrid ? "radial-gradient(#cbd5e1 1px, transparent 1px)" : "none",
              backgroundColor: showGrid ? "#f1f5f9" : "#e2e8f0",
            }}
          >
            <div
              className="pce-canvas-paper"
              style={{
                width: A4_W * editorZoom,
                height: A4_H * editorZoom,
              }}
            >
              <div ref={konvaContainerRef} className="pce-konva-stage-wrap" />

              {/* HTML Rich Text Overlays */}
              {activePage.layers.map((layer) => {
                if (layer.type !== "text" || layer.hidden) return null;
                const raw = layer.isPlaceholder ? `[${layer.placeholderLabel || "Placeholder"}]` : layer.content || "Text";
                let layerFontFamily = 'Arial, "Inter", sans-serif';
                if (layer.fontFamily) {
                  if (layer.fontFamily.toLowerCase().includes("times")) {
                    layerFontFamily = "Arial, sans-serif";
                  } else {
                    layerFontFamily = `${layer.fontFamily}, Arial, sans-serif`;
                  }
                }

                return (
                  <div
                    key={layer.id}
                    className="pce-canvas-html-overlay"
                    style={{
                      left: layer.x * editorZoom,
                      top: layer.y * editorZoom,
                      width: layer.w,
                      height: layer.h,
                      transform: `scale(${editorZoom})`,
                      transformOrigin: "top left",
                      backgroundColor: layer.fillColor && layer.fillColor !== "transparent" ? layer.fillColor : undefined,
                      borderRadius: (layer.borderRadius || 0) + "px",
                      padding: (layer.padding !== undefined ? layer.padding : 4) + "px",
                      border: layer.strokeWidth && layer.strokeColor ? `${layer.strokeWidth}px solid ${layer.strokeColor}` : undefined,
                      boxSizing: "border-box",
                      fontFamily: layerFontFamily,
                      fontSize: layer.fontSize || 16,
                      color: layer.color || "#000000",
                      fontWeight: layer.bold ? "bold" : "normal",
                      fontStyle: layer.italic ? "italic" : "normal",
                      textAlign: layer.align || "left",
                      lineHeight: layer.lineHeight || 1.2,
                      letterSpacing: (layer.letterSpacing || 0) + "px",
                    }}
                    dangerouslySetInnerHTML={{ __html: raw }}
                  />
                );
              })}
            </div>
          </div>

          {/* Right: Inspector Panel (Layers / Properties) */}
          <div className={`pce-inspector-panel ${rightPanelCollapsed ? "collapsed" : ""}`}>
            <div className="pce-inspector-header">
              <Segmented
                block
                size="small"
                value={sidePanel}
                onChange={setSidePanel}
                options={[
                  { label: "Layers", value: "layers", icon: <BarsOutlined /> },
                  { label: "Properties", value: "properties", icon: <SettingOutlined /> },
                ]}
              />
            </div>

            <div className="pce-inspector-content hide-scrollbar">
              {/* Layers Tab */}
              {sidePanel === "layers" && (
                <Flex vertical gap={6}>
                  <Flex justify="space-between" align="center" style={{ marginBottom: 4 }}>
                    <Text type="secondary" style={{ fontSize: "0.78rem" }}>
                      Page {activePageIndex + 1} ({sortedLayers.length} Layers)
                    </Text>
                  </Flex>

                  {sortedLayers.length === 0 && (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No layers on this page." style={{ margin: "30px 0" }}>
                      <Button size="small" icon={<PlusOutlined />} onClick={() => addTextLayer(false)}>
                        Add Text Layer
                      </Button>
                    </Empty>
                  )}

                  {sortedLayers.map((layer) => {
                    const isSelected = layer.id === selectedLayerId;

                    return (
                      <div key={layer.id} className={`pce-layer-item ${isSelected ? "selected" : ""} ${layer.hidden ? "hidden-layer" : ""}`} draggable onDragStart={(e) => onLayerDragStart(e, layer.id)} onDragOver={(e) => e.preventDefault()} onDrop={(e) => onLayerDrop(e, layer.id)}>
                        <button
                          type="button"
                          onClick={() => selectLayer(layer.id)}
                          style={{
                            background: "none",
                            border: "none",
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            flex: 1,
                            textAlign: "left",
                            cursor: "pointer",
                            padding: 0,
                            fontFamily: "inherit",
                            minWidth: 0,
                            overflow: "hidden",
                          }}
                        >
                          <DragOutlined style={{ color: "#94a3b8", cursor: "grab" }} />
                          {layer.type === "text" && layer.isPlaceholder && <TagOutlined style={{ color: "#7c3aed" }} />}
                          {layer.type === "text" && !layer.isPlaceholder && <FontSizeOutlined style={{ color: "#2563eb" }} />}
                          {layer.type === "image" && <PictureOutlined style={{ color: "#059669" }} />}
                          {layer.type === "pdf_page" && <FilePdfOutlined style={{ color: "#d97706" }} />}
                          {layer.type === "shape" && <BorderOutlined style={{ color: "#64748b" }} />}

                          <Text
                            ellipsis
                            style={{
                              fontSize: "0.82rem",
                              maxWidth: 130,
                              fontWeight: isSelected ? 600 : 400,
                            }}
                          >
                            {layerLabel(layer)}
                          </Text>
                        </button>

                        <Space size={2} onClick={(e) => e.stopPropagation()}>
                          <Tooltip title={layer.hidden ? "Show" : "Hide"}>
                            <Button type="text" size="small" icon={layer.hidden ? <EyeInvisibleOutlined /> : <EyeOutlined />} onClick={() => toggleLayerHidden(layer.id)} />
                          </Tooltip>

                          <Tooltip title={layer.locked ? "Unlock" : "Lock"}>
                            <Button type="text" size="small" icon={layer.locked ? <LockOutlined style={{ color: "#dc2626" }} /> : <UnlockOutlined />} onClick={() => toggleLayerLocked(layer.id)} />
                          </Tooltip>

                          {!layer.locked && (
                            <Popconfirm
                              title="Delete layer?"
                              okText="Yes"
                              cancelText="No"
                              onConfirm={() => {
                                setSelectedLayerId(layer.id);
                                deleteSelectedLayer();
                              }}
                            >
                              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                            </Popconfirm>
                          )}
                        </Space>
                      </div>
                    );
                  })}
                </Flex>
              )}

              {/* Properties Tab */}
              {sidePanel === "properties" && (
                <div>
                  {/* When no layer is selected: Show Page Details & Quick Actions instead of empty void! */}
                  {!selectedLayer && (
                    <Flex vertical gap={12}>
                      <Card size="small" styles={{ body: { padding: "12px 14px" } }} style={{ background: "#f8fafc", borderColor: "#e2e8f0" }}>
                        <Text strong style={{ fontSize: "0.85rem", display: "block", marginBottom: 6 }}>
                          Page Overview
                        </Text>
                        <Flex vertical gap={4}>
                          <Flex justify="space-between">
                            <Text type="secondary" style={{ fontSize: "0.78rem" }}>
                              Current Page:
                            </Text>
                            <Text strong style={{ fontSize: "0.78rem" }}>
                              {activePageIndex + 1} of {pages.length}
                            </Text>
                          </Flex>
                          <Flex justify="space-between">
                            <Text type="secondary" style={{ fontSize: "0.78rem" }}>
                              Dimensions:
                            </Text>
                            <Text style={{ fontSize: "0.78rem" }}>794 × 1123 px (A4)</Text>
                          </Flex>
                          <Flex justify="space-between">
                            <Text type="secondary" style={{ fontSize: "0.78rem" }}>
                              Layers on Page:
                            </Text>
                            <Text strong style={{ fontSize: "0.78rem" }}>
                              {activePage.layers.length}
                            </Text>
                          </Flex>
                        </Flex>
                      </Card>

                      <Card size="small" styles={{ body: { padding: "12px 14px" } }} style={{ borderColor: "#e2e8f0" }}>
                        <Text strong style={{ fontSize: "0.85rem", display: "block", marginBottom: 8 }}>
                          Page Management
                        </Text>
                        <Flex vertical gap={8}>
                          <Button icon={<CopyOutlined />} block onClick={() => duplicatePage(activePageIndex)}>
                            Duplicate Page {activePageIndex + 1}
                          </Button>
                          {pages.length > 1 && (
                            <Popconfirm title="Delete this page?" description="Are you sure? All layers on this page will be removed." okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }} onConfirm={() => deletePage(activePageIndex)}>
                              <Button danger block icon={<DeleteOutlined />}>
                                Delete Page {activePageIndex + 1}
                              </Button>
                            </Popconfirm>
                          )}
                        </Flex>
                      </Card>

                      <Card size="small" styles={{ body: { padding: "12px 14px" } }} style={{ borderColor: "#e2e8f0" }}>
                        <Text strong style={{ fontSize: "0.85rem", display: "block", marginBottom: 8 }}>
                          Quick Add
                        </Text>
                        <Row gutter={[8, 8]}>
                          <Col span={12}>
                            <Button size="small" block icon={<FontSizeOutlined />} onClick={() => addTextLayer(false)}>
                              Text
                            </Button>
                          </Col>
                          <Col span={12}>
                            <Button size="small" block icon={<TagOutlined />} onClick={() => addTextLayer(true)} style={{ color: "#7c3aed" }}>
                              Placeholder
                            </Button>
                          </Col>
                          <Col span={12}>
                            <Button size="small" block icon={<AppstoreOutlined />} onClick={useCompanyLogo}>
                              Logo
                            </Button>
                          </Col>
                          <Col span={12}>
                            <Button size="small" block icon={<BorderOutlined />} onClick={() => addShapeLayer("rect")}>
                              Shape
                            </Button>
                          </Col>
                        </Row>
                      </Card>
                    </Flex>
                  )}

                  {/* Text / Placeholder Properties */}
                  {selectedLayer?.type === "text" && (
                    <Flex vertical gap={12}>
                      {/* Placeholder Setting */}
                      <Card size="small" styles={{ body: { padding: "10px 12px" } }} style={{ background: "#f8fafc", borderColor: "#e2e8f0" }}>
                        <Flex justify="space-between" align="center">
                          <Text strong style={{ fontSize: "0.82rem" }}>
                            Employee Placeholder
                          </Text>
                          <Switch
                            size="small"
                            checked={propIsPlaceholder}
                            onChange={(val) => {
                              setPropIsPlaceholder(val);
                              applyProps({ isPlaceholder: val });
                            }}
                          />
                        </Flex>

                        {propIsPlaceholder && (
                          <Flex vertical gap={8} style={{ marginTop: 10 }}>
                            <div>
                              <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                                Lead Field
                              </Text>
                              <Select
                                size="small"
                                style={{ width: "100%", marginTop: 3 }}
                                value={propPlaceholderMode}
                                onChange={(mode) => {
                                  setPropPlaceholderMode(mode);
                                  const finalLabel = mode !== "custom" && mode !== "dropdown" ? mode : "";
                                  setPropPlaceholderLabel(finalLabel);
                                  applyProps({ placeholderMode: mode, placeholderLabel: finalLabel });
                                }}
                                options={PLACEHOLDER_SELECT_OPTIONS}
                              />
                            </div>

                            {(propPlaceholderMode === "custom" || propPlaceholderMode === "dropdown") && (
                              <div>
                                <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                                  Label
                                </Text>
                                <Input
                                  size="small"
                                  value={propPlaceholderLabel}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setPropPlaceholderLabel(val);
                                    applyProps({ placeholderLabel: val });
                                  }}
                                  placeholder="Enter custom placeholder label"
                                  style={{ marginTop: 3 }}
                                />
                              </div>
                            )}

                            {propPlaceholderMode === "dropdown" && (
                              <div>
                                <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                                  Pill Options (comma separated)
                                </Text>
                                <Input.TextArea
                                  value={propPlaceholderDropdownOptionsText}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setPropPlaceholderDropdownOptionsText(val);
                                    applyProps({ dropdownOptionsText: val });
                                  }}
                                  rows={2}
                                  placeholder="Option 1, Option 2, Option 3"
                                  style={{ marginTop: 3 }}
                                />
                              </div>
                            )}

                            <div>
                              <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                                Max Characters (optional)
                              </Text>
                              <InputNumber
                                size="small"
                                value={propPlaceholderMaxChars}
                                onChange={(val) => {
                                  setPropPlaceholderMaxChars(val);
                                  applyProps({ placeholderMaxChars: val });
                                }}
                                style={{ width: "100%", marginTop: 3 }}
                                placeholder="e.g. 100"
                              />
                            </div>
                          </Flex>
                        )}
                      </Card>

                      {/* Content Box */}
                      {!propIsPlaceholder && (
                        <div>
                          <Text strong style={{ fontSize: "0.82rem" }}>
                            Content
                          </Text>
                          <Flex align="center" gap={4} style={{ margin: "4px 0 6px" }}>
                            <Space.Compact size="small">
                              <Button icon={<BoldOutlined />} onClick={() => execFormat("bold")} />
                              <Button icon={<ItalicOutlined />} onClick={() => execFormat("italic")} />
                              <Button icon={<UnderlineOutlined />} onClick={() => execFormat("underline")} />
                              <Button icon={<UnorderedListOutlined />} onClick={() => execFormat("insertUnorderedList")} />
                            </Space.Compact>
                            <ColorPicker size="small" value={propColor} onChange={(_, hex) => execFormat("foreColor", hex)} />
                          </Flex>

                          <div
                            ref={editorAreaRef}
                            className="pce-richtext-box"
                            contentEditable
                            suppressContentEditableWarning
                            onInput={(e) => {
                              const html = e.currentTarget.innerHTML;
                              setPropContent(html);
                              applyProps({ content: html });
                            }}
                          />
                        </div>
                      )}

                      {/* Typography Section */}
                      <Card size="small" styles={{ body: { padding: "10px 12px" } }} style={{ borderColor: "#e2e8f0" }}>
                        <Text strong style={{ fontSize: "0.82rem", marginBottom: 6, display: "block" }}>
                          Typography
                        </Text>
                        <Row gutter={[6, 6]}>
                          <Col span={14}>
                            <Text type="secondary" style={{ fontSize: "0.72rem" }}>
                              Font Family
                            </Text>
                            <Select
                              size="small"
                              value={propFontFamily}
                              onChange={(val) => {
                                setPropFontFamily(val);
                                applyProps({ fontFamily: val });
                              }}
                              style={{ width: "100%", marginTop: 2 }}
                              options={FONT_FAMILY_OPTIONS}
                            />
                          </Col>
                          <Col span={10}>
                            <Text type="secondary" style={{ fontSize: "0.72rem" }}>
                              Font Size
                            </Text>
                            <InputNumber
                              size="small"
                              value={propFontSize}
                              onChange={(val) => {
                                setPropFontSize(val || 16);
                                applyProps({ fontSize: val || 16 });
                              }}
                              min={6}
                              max={120}
                              style={{ width: "100%", marginTop: 2 }}
                            />
                          </Col>
                        </Row>

                        <Row gutter={[6, 6]} style={{ marginTop: 6 }}>
                          <Col span={8}>
                            <Text type="secondary" style={{ fontSize: "0.72rem" }}>
                              Color
                            </Text>
                            <div style={{ marginTop: 2 }}>
                              <ColorPicker
                                size="small"
                                value={propColor}
                                onChange={(_, hex) => {
                                  setPropColor(hex);
                                  applyProps({ color: hex });
                                }}
                              />
                            </div>
                          </Col>
                          <Col span={8}>
                            <Text type="secondary" style={{ fontSize: "0.72rem" }}>
                              Line Height
                            </Text>
                            <InputNumber
                              size="small"
                              value={propLineHeight}
                              step={0.1}
                              min={0.8}
                              max={3}
                              onChange={(val) => {
                                setPropLineHeight(val || 1.2);
                                applyProps({ lineHeight: val || 1.2 });
                              }}
                              style={{ width: "100%", marginTop: 2 }}
                            />
                          </Col>
                          <Col span={8}>
                            <Text type="secondary" style={{ fontSize: "0.72rem" }}>
                              Letter Spacing
                            </Text>
                            <InputNumber
                              size="small"
                              value={propLetterSpacing}
                              min={-2}
                              max={10}
                              onChange={(val) => {
                                setPropLetterSpacing(val || 0);
                                applyProps({ letterSpacing: val || 0 });
                              }}
                              style={{ width: "100%", marginTop: 2 }}
                            />
                          </Col>
                        </Row>

                        <div style={{ marginTop: 8 }}>
                          <Text type="secondary" style={{ fontSize: "0.72rem", display: "block", marginBottom: 3 }}>
                            Alignment
                          </Text>
                          <Radio.Group
                            value={propAlign}
                            onChange={(e) => {
                              setPropAlign(e.target.value);
                              applyProps({ align: e.target.value });
                            }}
                            buttonStyle="solid"
                            size="small"
                          >
                            <Radio.Button value="left">
                              <AlignLeftOutlined />
                            </Radio.Button>
                            <Radio.Button value="center">
                              <AlignCenterOutlined />
                            </Radio.Button>
                            <Radio.Button value="right">
                              <AlignRightOutlined />
                            </Radio.Button>
                          </Radio.Group>
                        </div>

                        <Button size="small" icon={<ExpandOutlined />} onClick={autoFitText} block style={{ marginTop: 8, borderRadius: 6 }}>
                          Auto-Fit Box to Text
                        </Button>
                      </Card>

                      {/* Background & Padding */}
                      <Card size="small" styles={{ body: { padding: "10px 12px" } }} style={{ borderColor: "#e2e8f0" }}>
                        <Flex justify="space-between" align="center">
                          <Text strong style={{ fontSize: "0.82rem" }}>
                            Background & Padding
                          </Text>
                          <Switch
                            size="small"
                            checked={propHasBackground}
                            onChange={(val) => {
                              setPropHasBackground(val);
                              applyProps({ hasBackground: val });
                            }}
                          />
                        </Flex>

                        {propHasBackground && (
                          <Row gutter={[6, 6]} style={{ marginTop: 8 }}>
                            <Col span={8}>
                              <Text type="secondary" style={{ fontSize: "0.72rem" }}>
                                Fill
                              </Text>
                              <div style={{ marginTop: 2 }}>
                                <ColorPicker
                                  size="small"
                                  value={propFillColor}
                                  onChange={(_, hex) => {
                                    setPropFillColor(hex);
                                    applyProps({ fillColor: hex });
                                  }}
                                />
                              </div>
                            </Col>
                            <Col span={8}>
                              <Text type="secondary" style={{ fontSize: "0.72rem" }}>
                                Padding
                              </Text>
                              <InputNumber
                                size="small"
                                value={propPadding}
                                min={0}
                                max={80}
                                onChange={(val) => {
                                  setPropPadding(val || 0);
                                  applyProps({ padding: val || 0 });
                                }}
                                style={{ width: "100%", marginTop: 2 }}
                              />
                            </Col>
                            <Col span={8}>
                              <Text type="secondary" style={{ fontSize: "0.72rem" }}>
                                Radius
                              </Text>
                              <InputNumber
                                size="small"
                                value={propBorderRadius}
                                min={0}
                                max={50}
                                onChange={(val) => {
                                  setPropBorderRadius(val || 0);
                                  applyProps({ borderRadius: val || 0 });
                                }}
                                style={{ width: "100%", marginTop: 2 }}
                              />
                            </Col>
                          </Row>
                        )}
                      </Card>
                    </Flex>
                  )}

                  {/* Shape Properties */}
                  {selectedLayer?.type === "shape" && (
                    <Flex vertical gap={12}>
                      <Card size="small" styles={{ body: { padding: "10px 12px" } }} style={{ borderColor: "#e2e8f0" }}>
                        <Text strong style={{ fontSize: "0.82rem", marginBottom: 6, display: "block" }}>
                          Shape Styling
                        </Text>

                        <Row gutter={[6, 6]}>
                          <Col span={12}>
                            <Flex justify="space-between" align="center" style={{ marginBottom: 4 }}>
                              <Text type="secondary" style={{ fontSize: "0.72rem" }}>
                                Fill
                              </Text>
                              <Switch
                                size="small"
                                checked={propHasBackground}
                                onChange={(val) => {
                                  setPropHasBackground(val);
                                  applyProps({ hasBackground: val });
                                }}
                              />
                            </Flex>
                            {propHasBackground && (
                              <ColorPicker
                                size="small"
                                value={propFillColor}
                                onChange={(_, hex) => {
                                  setPropFillColor(hex);
                                  applyProps({ fillColor: hex });
                                }}
                              />
                            )}
                          </Col>

                          <Col span={12}>
                            <Text type="secondary" style={{ fontSize: "0.72rem", display: "block", marginBottom: 4 }}>
                              Stroke
                            </Text>
                            <ColorPicker
                              size="small"
                              value={propStrokeColor}
                              onChange={(_, hex) => {
                                setPropStrokeColor(hex);
                                applyProps({ strokeColor: hex });
                              }}
                            />
                          </Col>
                        </Row>

                        <Row gutter={[6, 6]} style={{ marginTop: 10 }}>
                          <Col span={12}>
                            <Text type="secondary" style={{ fontSize: "0.72rem" }}>
                              Stroke Width
                            </Text>
                            <InputNumber
                              size="small"
                              value={propStrokeWidth}
                              min={0}
                              max={20}
                              onChange={(val) => {
                                setPropStrokeWidth(val || 0);
                                applyProps({ strokeWidth: val || 0 });
                              }}
                              style={{ width: "100%", marginTop: 2 }}
                            />
                          </Col>
                          <Col span={12}>
                            <Text type="secondary" style={{ fontSize: "0.72rem" }}>
                              Corner Radius
                            </Text>
                            <InputNumber
                              size="small"
                              value={propBorderRadius}
                              min={0}
                              max={60}
                              onChange={(val) => {
                                setPropBorderRadius(val || 0);
                                applyProps({ borderRadius: val || 0 });
                              }}
                              style={{ width: "100%", marginTop: 2 }}
                            />
                          </Col>
                        </Row>
                      </Card>
                    </Flex>
                  )}

                  {/* Image / PDF Properties */}
                  {selectedLayer?.type === "image" && (
                    <Card size="small" styles={{ body: { padding: "10px" } }} style={{ borderColor: "#e2e8f0" }}>
                      <Text type="secondary" style={{ fontSize: "0.78rem" }}>
                        Image element. Reposition by dragging or resize using corner handles.
                      </Text>
                    </Card>
                  )}

                  {selectedLayer?.type === "pdf_page" && (
                    <Card size="small" styles={{ body: { padding: "10px" } }} style={{ borderColor: "#fde68a", background: "#fffbeb" }}>
                      <Text style={{ color: "#d97706", fontSize: "0.78rem" }}>📄 Imported PDF Background (locked). Add overlay placeholders, shapes, and images on top.</Text>
                    </Card>
                  )}

                  {/* Delete Button */}
                  {selectedLayer && !selectedLayer.locked && (
                    <Popconfirm title="Delete this layer?" okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }} onConfirm={deleteSelectedLayer}>
                      <Button danger block size="small" icon={<DeleteOutlined />} style={{ marginTop: 12, borderRadius: 6 }}>
                        Delete Layer
                      </Button>
                    </Popconfirm>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Balanced & Fully Utilized Studio Bottom Bar */}
        <div className="pce-studio-bottombar">
          <Flex justify="space-between" align="center" style={{ width: "100%" }}>
            {/* Left: Interactive Page Switcher & Indicator */}
            <Space size={6}>
              <Tooltip title="Previous Page">
                <Button size="small" icon={<LeftOutlined />} disabled={activePageIndex === 0} onClick={() => switchPage(activePageIndex - 1)} />
              </Tooltip>

              <Tag color="indigo" style={{ borderRadius: 6, margin: 0, fontWeight: 600, fontSize: "0.78rem" }}>
                Page {activePageIndex + 1} of {pages.length}
              </Tag>

              <Tooltip title="Next Page">
                <Button size="small" icon={<RightOutlined />} disabled={activePageIndex === pages.length - 1} onClick={() => switchPage(activePageIndex + 1)} />
              </Tooltip>

              <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={addPage} style={{ borderRadius: 6, fontSize: "0.75rem" }}>
                Add Page
              </Button>
            </Space>

            {/* Center: History & Screen-Fit Canvas Zoom Controls */}
            <Flex align="center" gap={12}>
              <Space.Compact size="small" style={{ borderRadius: 8 }}>
                <Tooltip title="Undo">
                  <Button icon={<UndoOutlined />} disabled={historyIndex <= 0} onClick={undo} />
                </Tooltip>
                <Tooltip title="Redo">
                  <Button icon={<RedoOutlined />} disabled={historyIndex >= history.length - 1} onClick={redo} />
                </Tooltip>
              </Space.Compact>

              <Divider type="vertical" style={{ height: 16, margin: "0 4px" }} />

              <Space.Compact size="small" style={{ borderRadius: 8 }}>
                <Tooltip title="Zoom Out">
                  <Button icon={<ZoomOutOutlined />} onClick={() => updateZoom(editorZoom - 0.05)} />
                </Tooltip>
                <Button style={{ minWidth: 54, textAlign: "center", padding: "0 6px", fontWeight: 600 }} onClick={fitToScreen} title="Click to Fit to Screen">
                  {Math.round(editorZoom * 100)}%
                </Button>
                <Tooltip title="Zoom In">
                  <Button icon={<ZoomInOutlined />} onClick={() => updateZoom(editorZoom + 0.05)} />
                </Tooltip>
              </Space.Compact>

              <Button size="small" icon={<CompressOutlined />} onClick={fitToScreen} style={{ borderRadius: 8, fontWeight: 500 }}>
                Fit Screen
              </Button>

              <Tooltip title={showGrid ? "Hide Grid Dots" : "Show Grid Dots"}>
                <Button size="small" type={showGrid ? "default" : "dashed"} icon={<BorderOutlined />} onClick={() => setShowGrid(!showGrid)} style={{ borderRadius: 8 }}>
                  Grid
                </Button>
              </Tooltip>
            </Flex>

            {/* Right: Export PDF & Save Template */}
            <Space size={8}>
              <Button size="small" icon={<DownloadOutlined />} onClick={exportToPdf} style={{ borderRadius: 6 }}>
                Export PDF
              </Button>

              <Button size="small" type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave} style={{ borderRadius: 6, backgroundColor: "#4f46e5", fontWeight: 500 }}>
                Save Template
              </Button>
            </Space>
          </Flex>
        </div>
      </div>
    </div>
  );
}

export default ProposalSettingsSection;
